import type { ChangeEvent } from 'react';
import type { FC } from '../../lib/teact/teact';
import React, {
  memo, useCallback, useEffect, useRef, useState,
} from '../../lib/teact/teact';
import { getActions, withGlobal } from '../../global';

import type { GlobalState } from '../../global/types';

import { pick } from '../../util/iteratees';
import { IS_TOUCH_ENV } from '../../util/windowEnvironment';
import { navigateBack } from './helpers/backNavigation';
import { formatAuthError } from './helpers/formatAuthError';

import useGeeTestCaptcha from '../../hooks/useGeeTestCaptcha';
import useLang from '../../hooks/useLang';
import useMultiaccountInfo from '../../hooks/useMultiaccountInfo';

import Button from '../ui/Button';
import Checkbox from '../ui/Checkbox';
import InputText from '../ui/InputText';

type StateProps = Pick<GlobalState, (
  'connectionState' | 'authState'
  | 'authIsLoading'
  | 'authIsLoadingQrCode' | 'authError'
  | 'authRememberMe'
)>;

const AuthEmailNumber: FC<StateProps> = ({
  connectionState,
  authState,
  authIsLoading,
  authIsLoadingQrCode,
  authError,
  authRememberMe,
}) => {
  const {
    setAuthPhoneNumber,
    clearAuthError,
    goToAuthQrCode,
    setAuthRememberMe,
  } = getActions();

  const lang = useLang();
  // eslint-disable-next-line no-null/no-null
  const emailInputRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef('');

  const isConnected = connectionState === 'connectionStateReady';
  const isAuthReady = authState === 'authorizationStateWaitPhoneNumber';
  const [email, setEmail] = useState('');
  const hasActiveAccount = Object.keys(useMultiaccountInfo()).length > 0;
  emailRef.current = email;

  const trimmedEmail = email.trim();
  const hasValidEmail = Boolean(trimmedEmail && trimmedEmail.includes('@'));
  const canSubmit = Boolean(isConnected && isAuthReady && hasValidEmail);

  const handleCaptchaSuccess = useCallback((validateResult: any) => {
    setAuthPhoneNumber({
      phoneNumber: emailRef.current.trim(),
      captchaValidate: validateResult,
    });
  }, [setAuthPhoneNumber]);

  const {
    isCaptchaLoading, captchaError, requestCaptcha, clearCaptchaError,
  } = useGeeTestCaptcha(handleCaptchaSuccess);

  useEffect(() => {
    if (!IS_TOUCH_ENV && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, []);

  const handleEmailChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (authError) {
      clearAuthError();
    }
    if (captchaError) {
      clearCaptchaError();
    }
    setEmail(e.target.value);
  }, [authError, captchaError, clearAuthError, clearCaptchaError]);

  const handleKeepSessionChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setAuthRememberMe(e.target.checked);
  }, [setAuthRememberMe]);

  const handleQrLogin = useCallback(() => {
    if (isCaptchaLoading || authIsLoading) {
      return;
    }
    goToAuthQrCode();
  }, [authIsLoading, goToAuthQrCode, isCaptchaLoading]);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (authIsLoading || authIsLoadingQrCode || isCaptchaLoading || !canSubmit) {
      return;
    }

    clearCaptchaError();
    requestCaptcha();
  }

  const error = formatAuthError(lang, authError || captchaError);

  return (
    <div id="auth-email-form" className="custom-scroll">
      {hasActiveAccount && (
        <Button
          size="smaller"
          round
          color="translucent"
          className="auth-close"
          ariaLabel="Back"
          onClick={navigateBack}
        >
          <i className="icon icon-close" />
        </Button>
      )}
      <div className="auth-form">
        <div id="logo" />
        <h1>{process.env.PROJECT_NAME}</h1>
        <form className="form" action="" onSubmit={handleSubmit}>
          <InputText
            ref={emailInputRef}
            id="sign-in-email"
            label={lang('BoundEmail')}
            value={email}
            inputMode="email"
            error={error}
            onChange={handleEmailChange}
            autoComplete="email"
          />

          <Checkbox
            id="sign-in-keep-session"
            label={lang('AuthKeepSignedIn')}
            checked={Boolean(authRememberMe)}
            onChange={handleKeepSessionChange}
          />

          {canSubmit && (
            <Button
              type="submit"
              ripple
              disabled={authIsLoading || authIsLoadingQrCode || isCaptchaLoading}
              isLoading={authIsLoading || isCaptchaLoading}
            >
              {lang('Login.Next')}
            </Button>
          )}

          {isAuthReady && (
            <Button
              isText
              ripple
              disabled={authIsLoading || isCaptchaLoading}
              isLoading={authIsLoadingQrCode}
              onClick={handleQrLogin}
            >
              {lang('Login.QR.Login')}
            </Button>
          )}
        </form>
      </div>
    </div>
  );
};

export default memo(withGlobal(
  (global): StateProps => pick(global, [
    'connectionState',
    'authState',
    'authIsLoading',
    'authIsLoadingQrCode',
    'authError',
    'authRememberMe',
  ]),
)(AuthEmailNumber));
